import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { FaCloudUploadAlt, FaTimes, FaSpinner } from 'react-icons/fa';
import { authService } from '../../services/apiService';
import { getUploadErrorMessage } from '../auth/partnerUploadFile';
import { ONBOARDING_THEME } from './onboardingTheme';

const Wrap = styled.div` margin-bottom: 10px; min-width: 0; `;
const Label = styled.label` display: block; font-size: 11px; font-weight: 600; color: ${ONBOARDING_THEME.text}; margin-bottom: 4px; `;
const Zone = styled.label`
  display: flex; align-items: center; justify-content: center; gap: 8px;
  min-height: 80px; padding: 12px; border: 2px dashed ${(p) => (p.drag ? ONBOARDING_THEME.accent : ONBOARDING_THEME.border)};
  border-radius: ${ONBOARDING_THEME.radiusSm}; background: ${(p) => (p.drag ? ONBOARDING_THEME.accentLight : ONBOARDING_THEME.inputBg)};
  cursor: pointer; transition: all 0.2s; color: ${ONBOARDING_THEME.textMuted};
  &:hover { border-color: ${ONBOARDING_THEME.accent}; color: ${ONBOARDING_THEME.accent}; background: ${ONBOARDING_THEME.accentLight}; }
`;
const HiddenInput = styled.input` display: none; `;
const FileName = styled.span` font-size: 12px; color: ${ONBOARDING_THEME.text}; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; `;
const ClearBtn = styled.button`
  padding: 4px 8px; border: none; background: ${ONBOARDING_THEME.errorLight}; color: ${ONBOARDING_THEME.error};
  border-radius: 6px; font-size: 11px; cursor: pointer; display: inline-flex; align-items: center; gap: 4px;
  &:hover { opacity: 0.9; }
`;
const Err = styled.p` font-size: 11px; color: ${ONBOARDING_THEME.error}; margin: 4px 0 0 0; `;

export default function FileUploadField({ label, value, onChange, accept = 'image/*,.pdf', required }) {
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setErr('');
    setUploading(true);
    try {
      const url = await authService.uploadCompressedFile(file);
      onChange(url);
    } catch (e) {
      setErr(getUploadErrorMessage(e) || e.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onInputChange = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const onDragOver = (e) => { e.preventDefault(); setDrag(true); };
  const onDragLeave = () => setDrag(false);

  const clear = (e) => { e.preventDefault(); onChange(''); };

  const hasFile = !!value;

  return (
    <Wrap>
      {label && <Label>{label}{required ? ' *' : ''}</Label>}
      <Zone drag={drag} onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}>
        <HiddenInput ref={inputRef} type="file" accept={accept} onChange={onInputChange} disabled={uploading} />
        {uploading ? (
          <><FaSpinner className="fa-spin" size={20} color={ONBOARDING_THEME.accent} /> <span>Compressing & uploading…</span></>
        ) : hasFile ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <FileName title={value}>{value.split('/').pop() || 'Uploaded'}</FileName>
            <ClearBtn type="button" onClick={clear}> <FaTimes size={10} /> Clear </ClearBtn>
          </span>
        ) : (
          <><FaCloudUploadAlt size={22} /> <span>Drop file here or click to upload</span></>
        )}
      </Zone>
      {err && <Err>{err}</Err>}
    </Wrap>
  );
}
